import React from 'react';
import { Ship, Truck, Plane, AlertTriangle } from 'lucide-react';
import Badge from './Badge';

const TRANSPORT_ICON = {
  reefer: { Icon: Ship,  bg: 'bg-sky-50',    text: 'text-sky-800'    },
  route:  { Icon: Truck, bg: 'bg-orange-50', text: 'text-orange-700' },
  air:    { Icon: Plane, bg: 'bg-sky-50',    text: 'text-sky-700'    },
};

function TransportIcon({ mode = 'reefer', size = 5 }) {
  const { Icon, bg, text } = TRANSPORT_ICON[mode] || TRANSPORT_ICON.reefer;
  return (
    <span className={`inline-flex items-center justify-center w-${size} h-${size} rounded ${bg} ${text} flex-shrink-0`}>
      <Icon size={size === 5 ? 12 : 14} strokeWidth={1.75} />
    </span>
  );
}

export function ShipmentCard({
  reference,
  product,
  origin,
  destination,
  weight,
  status,
  progress,
  eta,
  temperature,
  container,
  mode = 'reefer',
  alertMessage,
  onClick,
}) {
  const hasIncident = status === 'incident';
  return (
    <div
      onClick={onClick}
      className={`bg-white border rounded-xl p-4 transition cursor-pointer ${
        hasIncident
          ? 'border-rose-200 hover:shadow-sm'
          : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[11px] text-slate-400 font-mono mb-0.5">{reference}</div>
          <div className="text-[14px] font-semibold text-slate-900">{product}</div>
          <div className="flex items-center gap-1.5 text-[12px] text-slate-500 mt-1">
            <TransportIcon mode={mode} size={5} />
            <span className="font-medium text-slate-700">{origin}</span>
            <span className="text-slate-400">→</span>
            <span className="font-medium text-slate-700">{destination}</span>
            {weight && <span className="text-slate-400">· {weight}</span>}
          </div>
        </div>
        <Badge variant={status} className="flex-shrink-0">
          {STATUS_LABEL[status] || status}
        </Badge>
      </div>

      {hasIncident && alertMessage ? (
        <div className="bg-rose-50 border border-rose-200 rounded-md px-3 py-2 text-[12px] text-rose-700 flex items-center gap-2">
          <AlertTriangle size={13} strokeWidth={1.75} />
          {alertMessage}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-teal-600 rounded-full"
                  style={{ width: `${progress ?? 0}%` }}
                />
              </div>
            </div>
            {(progress != null || eta) && (
              <span className="text-[11px] text-slate-500 flex-shrink-0">
                {progress != null && `${progress}%`}{eta && ` · ETD ${eta}`}
              </span>
            )}
          </div>
          {(temperature || container) && (
            <div className="mt-2 text-[11.5px] text-slate-400 font-mono">
              {[temperature, container].filter(Boolean).join(' · ')}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const STATUS_LABEL = {
  preparing:  'En préparation',
  transit:    'En transit',
  delivered:  'Livré',
  incident:   'Incident',
  pending:    'À régler',
  paid:       'Payée',
  overdue30:  'Échue 1–30j',
  overdue60:  'Échue +30j',
  disputed:   'Contestée',
};

export function Card({ title, headerRight, footer, children, className = '' }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl overflow-hidden ${className}`}>
      {(title || headerRight) && (
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
          <span className="font-semibold text-[13px]">{title}</span>
          {headerRight}
        </div>
      )}
      <div>{children}</div>
      {footer && (
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200">{footer}</div>
      )}
    </div>
  );
}

export function CardRow({ icon: Icon, title, subtitle, valueMain, valueSub, valueDanger }) {
  return (
    <div className="px-5 py-3 flex items-center gap-3 border-b border-slate-100 last:border-0">
      {Icon && (
        <div className="w-8 h-8 rounded-md bg-slate-50 border border-slate-200 grid place-items-center text-slate-500 flex-shrink-0">
          <Icon size={14} strokeWidth={1.75} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] font-semibold text-slate-900">{title}</div>
        {subtitle && <div className="text-[11.5px] text-slate-500">{subtitle}</div>}
      </div>
      <div className="text-right flex-shrink-0">
        <div className={`text-[13px] font-semibold tabular-nums ${valueDanger ? 'text-rose-600' : 'text-slate-900'}`}>
          {valueMain}
        </div>
        {valueSub && (
          <div className={`text-[11px] ${valueDanger ? 'text-rose-500' : 'text-slate-400'}`}>
            {valueSub}
          </div>
        )}
      </div>
    </div>
  );
}

export default ShipmentCard;
